/* eslint-disable @typescript-eslint/no-explicit-any */
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Stack,
    AlertTitle,
    Box,
} from "@mui/material"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import {
    ReactElement,
    SyntheticEvent,
    useContext,
    useState,
    useEffect,
    useRef,
    useMemo,
} from "react"
import { Control, Controller, useFormContext } from "react-hook-form"

import { Datasets, LCTOnlyDatasets } from "../../../../constants/enums"
import { SUPPLEMENTING_INFO_KW } from "../../../../constants/generalConstants"
import {
    SINGLE_DEMAND_DEVICE_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../../../router/routes"
import useSearchLct from "../../../../services/middleware/hooks/singleApplication/useSearchLct"
import { IDeviceSearchResult } from "../../../../services/middleware/types/singleApplication/searchLctTypes"
import { ITEM_BORDER_MULTIPLIER } from "../../../../themes/defaultThemeParams"
import { snackbar } from "../../../../utils/general/generalUtils"
import { isV2gEvcpContext } from "../../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../../buttons/appButton"
import AppAlert from "../../../dataDisplay/alerts/appAlert"
import VerticalKeyValueList from "../../../dataDisplay/verticalKeyValueList"
import DisplayIf from "../../../wrappers/displayIf"
import { ApplicationContext } from "../applicationProvider"
import { SupplyPhases } from "../types/formGeneratorTypes"

export const SUPPLEMENTING_KEY = "deviceSysRefSearchResult"
const INPUT_EVENT_TYPE = "input"

interface ISelectNewDeviceProps {
    registerName: string
    control: Control
    programmaticallySetForm: (key: string, value: any) => void
    watch: (key: string) => any
    setClass: () => void
    deviceType: string
    dataset: Datasets
    supplementingInfoKey: string
    phases?: SupplyPhases
}

/**
 * Renders a device search field that allows the user to search for a device
 * The user can then select a device from the autocomplete suggestions
 */
const SelectNewDevice = ({
    registerName,
    control,
    programmaticallySetForm,
    watch,
    setClass,
    deviceType,
    dataset,
    supplementingInfoKey,
    phases,
}: ISelectNewDeviceProps): ReactElement => {
    // Get form methods from context to access setValue
    const formMethods = useFormContext()

    // Extract initial value from supplementing info
    const supplementingInfo = watch(supplementingInfoKey)
    const defaultFormValue = supplementingInfo?.[SUPPLEMENTING_KEY] ?? undefined

    // Track if the device has already been processed to prevent loops
    const deviceProcessedRef = useRef(false)

    const { jurisdiction, lcts } = useContext(ApplicationContext)

    // Set initial value only on first render
    const [searchTerm, setSearchTerm] = useState("")
    // Add a new state to track if user is actively searching/editing
    const [isEditing, setIsEditing] = useState(false)
    const autocompleteRef = useRef<any>(null)

    const [displayDetails, setDisplayDetails] =
        useState<IDeviceSearchResult | null>(defaultFormValue)

    useEffect(() => {
        const currentDeviceRef =
            supplementingInfo?.[SUPPLEMENTING_KEY]?.deviceRef
        const displayDeviceRef = displayDetails?.deviceRef

        if (
            supplementingInfo?.[SUPPLEMENTING_KEY] &&
            currentDeviceRef !== displayDeviceRef &&
            !deviceProcessedRef.current
        ) {
            deviceProcessedRef.current = true

            // Update display details
            setDisplayDetails(supplementingInfo[SUPPLEMENTING_KEY])

            // Use formMethods.setValue if available
            if (formMethods && currentDeviceRef) {
                formMethods.setValue(registerName, currentDeviceRef, {
                    shouldValidate: true,
                    shouldDirty: true,
                })
            }

            if (currentDeviceRef && !watch(registerName)) {
                setClass()
            }
        }

        // Reset the processed flag when the supplementing info changes
        return () => {
            if (currentDeviceRef !== displayDeviceRef) {
                deviceProcessedRef.current = false
            }
        }
    }, [supplementingInfo])

    const { data, isLoading, isError } = useSearchLct(
        searchTerm,
        deviceType,
        dataset,
        jurisdiction,
        phases
    )

    // Important: Create a memoized empty array that will never change
    const emptyArray = useMemo(() => [], [])

    // PBI 71845: in V2G applications, hide EVCPs that are not linked to a V2G
    // inverter (no V2G_TTRStatus). The submit-time validator (API-Hybrid
    // V2G_EVCP_NOT_LINKED) is the source of truth; this filter just keeps
    // installers off a doomed journey. Scoped to EVAC dataset via the shared
    // isV2gEvcpContext predicate so this surface and the alert/manual-entry
    // gates in selectDeviceForm cannot drift.
    const isV2gEvcpPicker = isV2gEvcpContext({ lcts, dataset })

    const options = useMemo(() => {
        const raw = Array.isArray(data) ? data : emptyArray
        if (!isV2gEvcpPicker) {
            return raw
        }
        return raw.filter((row) => !!row.v2gTtrStatus)
    }, [data, emptyArray, isV2gEvcpPicker])

    /** Called when user types in the address search field to fetch suggestions */
    const handleSearch = (
        _event: SyntheticEvent<Element, Event>,
        value: string,
        reason: string
    ): void => {
        if (reason === INPUT_EVENT_TYPE) {
            setIsEditing(true)
            setSearchTerm(value)
        } else if (reason === "reset") {
            setIsEditing(false)
        } else if (reason === "clear") {
            setIsEditing(false)
            setSearchTerm("")
        }
    }

    /** Called when user selects an address from the autocomplete suggestions */
    const handleSelect = (
        _event: SyntheticEvent<Element, Event>,
        value: IDeviceSearchResult | null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formSet: (...event: any[]) => void
    ): void => {
        setIsEditing(false)

        // If value is null, the user has cleared the selection
        if (value !== null) {
            formSet(value.deviceRef)
            setClass()
        } else {
            // Clear the field value when user removes the selection
            formSet(null)

            // Clear the supplementing info to ensure complete removal
            programmaticallySetForm(SUPPLEMENTING_INFO_KW, {
                [SUPPLEMENTING_KEY]: null,
            })

            // Reset the deviceProcessedRef to ensure future selections work
            deviceProcessedRef.current = false
        }
        setDisplayDetails(value)

        // Update supplementing info
        programmaticallySetForm(SUPPLEMENTING_INFO_KW, {
            [SUPPLEMENTING_KEY]: value,
        })
    }

    // Enhanced clear handler to ensure proper clearing
    const handleClear = (): void => {
        deviceProcessedRef.current = false

        if (formMethods) {
            formMethods.setValue(registerName, null, {
                shouldValidate: false, // Changed from true to false
                shouldDirty: true,
            })
        }

        setDisplayDetails(null)
        setSearchTerm("")

        formMethods?.setValue(
            supplementingInfoKey,
            {
                [SUPPLEMENTING_KEY]: null,
            },
            {
                shouldValidate: false, // Changed from true to false
                shouldDirty: true,
            }
        )

        programmaticallySetForm(SUPPLEMENTING_INFO_KW, {
            [SUPPLEMENTING_KEY]: null,
        })
    }

    const requiredRule = {
        required: { value: true, message: "Please select an option" },
    }

    /** Routes user to single device summary page */
    const navigateToSingleDevice = (): void => {
        if (typeof displayDetails?.deviceRef === "string") {
            if (dataset === Datasets.TTR) {
                window.open(
                    SINGLE_GEN_DEVICE_NAV_LINK(displayDetails.deviceRef),
                    "_blank"
                )
            } else {
                window.open(
                    SINGLE_DEMAND_DEVICE_NAV_LINK(
                        dataset as unknown as LCTOnlyDatasets,
                        displayDetails.deviceRef
                    ),
                    "_blank"
                )
            }
        } else {
            snackbar(
                "Something went wrong! It was not possible to take you to the device summary page",
                "warning"
            )
        }
    }

    return (
        <Stack spacing={4} width="100%" paddingBottom={2}>
            <Controller
                name={registerName}
                rules={requiredRule}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                        ref={autocompleteRef}
                        options={options}
                        filterOptions={(opts) => opts}
                        loading={isLoading}
                        isOptionEqualToValue={(option, value) =>
                            option?.deviceRef === value?.deviceRef
                        }
                        getOptionLabel={(option) => option?.displayValue || ""}
                        onChange={(e, v) => handleSelect(e, v, field.onChange)}
                        onInputChange={handleSearch}
                        value={displayDetails}
                        inputValue={
                            isEditing
                                ? searchTerm
                                : displayDetails?.displayValue || searchTerm
                        }
                        clearOnBlur={false}
                        clearOnEscape
                        disableClearable={false}
                        forcePopupIcon={displayDetails ? false : undefined}
                        key={`autocomplete-${
                            displayDetails?.deviceRef || "empty"
                        }`}
                        clearIcon={
                            <ClearOutlinedIcon
                                style={{
                                    opacity: displayDetails ? 1 : 0,
                                    cursor: "pointer",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation() // Prevent event bubbling
                                    handleClear()
                                }}
                            />
                        }
                        renderOption={(props, option, { inputValue }) => {
                            const matches = match(
                                String(option.displayValue),
                                inputValue,
                                {
                                    insideWords: true,
                                }
                            )
                            const parts = parse(
                                String(option.displayValue),
                                matches
                            )

                            return (
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                <li {...props}>
                                    <div>
                                        {parts.map((part, index) => (
                                            <span
                                                key={
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    part.text +
                                                    part.highlight +
                                                    index
                                                }
                                                style={{
                                                    fontWeight: part.highlight
                                                        ? 700
                                                        : 400,
                                                }}
                                            >
                                                {part.text}
                                            </span>
                                        ))}
                                    </div>
                                </li>
                            )
                        }}
                        renderInput={(params) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const {
                                InputLabelProps,
                                InputProps,
                                ...otherParams
                            } = params

                            return (
                                <TextField
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...otherParams}
                                    label="Device Search"
                                    placeholder="Start typing to search for a device..."
                                    error={!!error || isError}
                                    helperText={error?.message}
                                    InputProps={{
                                        ...InputProps,
                                        sx: {
                                            borderRadius:
                                                ITEM_BORDER_MULTIPLIER,
                                        },
                                        endAdornment: (
                                            <>
                                                {isLoading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={20}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )
                        }}
                        componentsProps={{
                            clearIndicator: {
                                sx: {
                                    visibility: displayDetails
                                        ? "visible"
                                        : "hidden",
                                    opacity: displayDetails ? 1 : 0,
                                    cursor: "pointer",
                                    "&:hover": {
                                        background: "rgba(0, 0, 0, 0.04)",
                                    },
                                },
                                onClick: (e) => {
                                    e.stopPropagation() // Prevent event bubbling
                                    handleClear()
                                },
                            },
                        }}
                    />
                )}
            />
            <DisplayIf condition={!!watch(registerName) || !!displayDetails}>
                <AppAlert severity="info">
                    <AlertTitle>Selected Device Details</AlertTitle>
                    <VerticalKeyValueList
                        overrideColour="palette.primary.main"
                        kwargs={{
                            "Device Ref": displayDetails?.deviceRef,
                            "Device Model No": displayDetails?.deviceModelNo,
                            "Number of Phases": displayDetails?.devicePhases,
                            Manufacturer: displayDetails?.deviceManufacturer,
                            [String(displayDetails?.deviceSizeName)]:
                                `${displayDetails?.deviceSize} ${displayDetails?.deviceSizeUnit}`,
                        }}
                    />
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        flexDirection="row"
                        marginTop={2}
                    >
                        <AppButton
                            endIcon={<NavigateNextIcon />}
                            variant="outlined"
                            onClick={navigateToSingleDevice}
                        >
                            View Device Details
                        </AppButton>
                    </Box>
                </AppAlert>
            </DisplayIf>
        </Stack>
    )
}

SelectNewDevice.defaultProps = {
    phases: 3,
}

export default SelectNewDevice
